import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Baby, Mail, Lock, AlertCircle, ArrowLeft } from 'lucide-react';
import { getDashboardPath } from '../lib/auth';

export const LoginPage = () => {
  const { login, isAuthenticated, user, isLoading } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5A67D8]"></div>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return <Navigate to={getDashboardPath(user.role)} replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      // Safari DataCloneError workaround
      let message = (typeof err === 'object' && err !== null)
        ? String(err.message || err.detail || 'Giriş yapılırken bir hata oluştu')
        : String(err || 'Giriş yapılırken bir hata oluştu');
      // iOS Safari leaks "The object can not be cloned." from emergent-main.js
      // postMessage(Request) — filter it so the user sees a real reason.
      const lower = message.toLowerCase();
      if (lower.indexOf('cloned') !== -1 || err?.name === 'DataCloneError') {
        message = 'Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7FC] flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#5A67D8] to-[#4338CA] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-8 backdrop-blur-sm">
            <Baby className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            NeoBreed AI
          </h1>
          <p className="text-xl text-white/80 text-center max-w-md" style={{ fontFamily: 'Inter, sans-serif' }}>
            Fetal yüz görüntüleme sisteminize hoşgeldiniz
          </p>
          <div className="mt-12 grid grid-cols-2 gap-6 w-full max-w-md">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-3xl font-bold mb-1">10K+</p>
              <p className="text-sm text-white/70">Mutlu Aile</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-3xl font-bold mb-1">500+</p>
              <p className="text-sm text-white/70">Klinik</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-3xl font-bold mb-1">99.2%</p>
              <p className="text-sm text-white/70">Memnuniyet</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-3xl font-bold mb-1">24/7</p>
              <p className="text-sm text-white/70">Destek</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link 
            to="/" 
            className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ana Sayfaya Dön
          </Link>

          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center">
              <div className="w-12 h-12 rounded-xl bg-[#5A67D8] flex items-center justify-center mx-auto mb-4 lg:hidden">
                <Baby className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Giriş Yap
              </CardTitle>
              <CardDescription className="text-slate-500">
                Hesabınıza giriş yapın
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-posta</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="ornek@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Şifre</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <button type="button" className="text-sm text-[#5A67D8] hover:underline">
                    Şifremi Unuttum
                  </button>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#5A67D8] hover:bg-[#4F46E5] text-white h-11"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Giriş Yapılıyor...</span>
                    </div>
                  ) : (
                    'Giriş Yap'
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Hesabınız yok mu?{' '}
                <Link to="/register" className="text-[#5A67D8] hover:underline font-medium">
                  Ücretsiz Kayıt Ol
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
